import type { Schedule } from '@hedepy/common/schemas/modules/schedule/schedule'
import type { SchedulePostRequest, SchedulePutRequest } from '@hedepy/common/schemas/routes/api/v2/schedule'
import { ofType } from 'redux-observable'
import { from, of } from 'rxjs'
import { catchError, mergeMap, switchMap } from 'rxjs/operators'

import GeneralActions from '../redux/GeneralRedux'
import TherapistActions, { TherapistTypes } from '../redux/TherapistRedux'
import { type ApiEndpoints } from '../services/api'
import { generalErrorHandler, generalSuccessHandler } from './EpicHelpers'

export default class TherapistEpic {
  static getAllClients = (api: ApiEndpoints) => (action$: any) =>
    action$.pipe(
      ofType(TherapistTypes.GET_ALL_CLIENTS_REQUEST),
      switchMap(() =>
        from(api.getAllClients()).pipe(
          mergeMap((response: any) =>
            from([
              GeneralActions.onStopFetching(['clients']),
              TherapistActions.onChangeTherapistValue('clients', response.data),
            ]),
          ),
          catchError(generalErrorHandler('clients')),
        ),
      ),
    )
  static addSchedule = (api: ApiEndpoints) => (action$: any) =>
    action$.pipe(
      ofType(TherapistTypes.ADD_SCHEDULE_REQUEST),
      switchMap(({ data }: { data: SchedulePostRequest }) =>
        from(api.addSchedule(data)).pipe(
          switchMap(() => of(TherapistActions.getScheduleRequest())),
          catchError(generalErrorHandler()),
        ),
      ),
    )
  static editSchedule = (api: ApiEndpoints) => (action$: any) =>
    action$.pipe(
      ofType(TherapistTypes.EDIT_SCHEDULE_REQUEST),
      switchMap(({ id, data }: { id: number; data: Omit<SchedulePutRequest, 'id'> }) =>
        from(api.editSchedule(id, data)).pipe(
          switchMap(() =>
            from([
              generalSuccessHandler('component.toaster.editScheduleSuccess'),
              TherapistActions.getScheduleRequest(),
            ]),
          ),
          catchError(generalErrorHandler()),
        ),
      ),
    )
  static getTherapistData = (api: ApiEndpoints) => (action$: any) =>
    action$.pipe(
      ofType(TherapistTypes.GET_THERAPIST_DATA_REQUEST),
      switchMap(({ therapistUserId }: { therapistUserId: number }) =>
        from(api.getTherapistData(therapistUserId)).pipe(
          switchMap((response: any) => of(TherapistActions.onMergeTherapistValue(response.data))),
          catchError(generalErrorHandler()),
        ),
      ),
    )
  static getSchedule = (api: ApiEndpoints) => (action$: any) =>
    action$.pipe(
      ofType(TherapistTypes.GET_SCHEDULE_REQUEST),
      mergeMap(() =>
        from(api.getSchedule()).pipe(
          switchMap(({ data }: { data: Schedule[] }) =>
            from([
              generalSuccessHandler('', 'therapistSchedule'),
              TherapistActions.onChangeTherapistValue('schedule', data),
            ]),
          ),
          catchError(generalErrorHandler('therapistSchedule')),
        ),
      ),
    )
  static deleteSchedule = (api: ApiEndpoints) => (action$: any) =>
    action$.pipe(
      ofType(TherapistTypes.DELETE_SCHEDULE_REQUEST),
      switchMap(({ id }: { id: number }) =>
        from(api.deleteSchedule(id)).pipe(
          switchMap(() =>
            from([
              generalSuccessHandler('component.toaster.deleteScheduleSuccess'),
              TherapistActions.getScheduleRequest(),
            ]),
          ),
          catchError(generalErrorHandler()),
        ),
      ),
    )
  static updateTherapistData = (api: ApiEndpoints) => (action$: any) =>
    action$.pipe(
      ofType(TherapistTypes.UPDATE_THERAPIST_DATA_REQUEST),
      switchMap(({ data }) =>
        from(api.updateTherapistData(data)).pipe(
          mergeMap(() =>
            from([
              generalSuccessHandler('component.toaster.therapistDataUpdateSuccess', 'updateTherapistData'),
              TherapistActions.onMergeTherapistValue(data),
            ]),
          ),
          catchError(generalErrorHandler('updateTherapistData')),
        ),
      ),
    )

  static getAllActions = ({ api }: { api: ApiEndpoints }) => [
    TherapistEpic.getAllClients(api),
    TherapistEpic.addSchedule(api),
    TherapistEpic.editSchedule(api),
    TherapistEpic.deleteSchedule(api),
    TherapistEpic.getSchedule(api),
    TherapistEpic.getTherapistData(api),
    TherapistEpic.updateTherapistData(api),
  ]
}
